/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import { ccDisplayKey, DisplayKey } from '../../../Game/DisplayKey';
import GameSetup from '../../../Game/GameSetup';
import StateDependencyComponent from '../../../StateManager/StateDependencyComponent';
import StateEvent from '../../../StateManager/StateEvent';
import { StateID } from '../../../StateManager/StateID';
import StateManager from '../../../StateManager/StateManager';
import StateReference from '../../../StateManager/StateReference';
import InjectableComponent from '../ComponentReference/InjectableComponent';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/** Base class for components that interact based on the configuration supplied by the RGS server. */
@cc._decorator.ccclass
export default abstract class OptionalBaseComponent extends StateDependencyComponent
{
    @property({ visible: false, override: true, serializable: false })
    protected _isActive: boolean = false;

    /** The state on which to check if the component should be shown. */
    @property({ type: StateReference, visible: true })
    private _checkOnState: StateReference = new StateReference(StateID.PostInitializing, true);
    public set CheckOnStateId(id: StateID) { this._checkOnState.StateID = id; }

    /** The DisplayKey to check the configuration for. */
    @property({ type: ccDisplayKey, visible: true })
    private _displayKey: DisplayKey = DisplayKey.AutoSpin;
    public set DisplayKey(value: DisplayKey) { this._displayKey = value; }

    /** @hidden */
    private _gameSetupRef: InjectableComponent<GameSetup> = new InjectableComponent();

    /** @hidden */
    private _stateManagerRef: InjectableComponent<StateManager> = new InjectableComponent();

    /** @hidden */
    protected setContexts(): void
    {
        super.setContexts();
        this._stateManagerRef.SetContext(StateManager);
        this._gameSetupRef.SetContext(GameSetup);
    }

    /** @hidden */
    protected start(): void
    {
        super.start(this._checkOnState);
    }

    /** @hidden */
    public onStateChangeHandler = (event: StateEvent): void =>
    {
        if (event.State === this._checkOnState.Value)
        {
            this.trigger();
            this.completeState(this._checkOnState.Value);
        }
    }

    /** @hidden */
    protected onEnable(): void
    {
        if (this._stateManagerRef.Value &&
            this._stateManagerRef.Value.CurrentState &&
            this._stateManagerRef.Value.CurrentState.StateID !== StateID.Loading &&
            this._stateManagerRef.Value.CurrentState.StateID !== StateID.Initializing)
        {
            this.trigger();
        }
    }

    protected isDisplayKeyValid(key: DisplayKey = this._displayKey): boolean
    {
        return this._gameSetupRef.WithValue(gameSetup => gameSetup.ShouldDisplay(key), false);
    }

    /** @hidden */
    protected abstract trigger(): void;
}
